import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCircleLocationArrow, faEye } from '@fortawesome/pro-light-svg-icons';
import { IconButton, Badge } from '@nexelem/components';
import { NodeIcon } from '../../../../../../../../components/organisms/ProductionPlans/Gantt/components';
import { GanttUtils } from '../../../../../../../../components/organisms/ProductionPlans/utils';
import { useCustomTranslation } from '../../../../../../../../hooks';
import { useDateFormatter } from '@nexelem/utils';
import { openTaskDetailsDrawer } from '../../../TaskDetailsDrawer';
import { useProductionTaskLocate } from '../../../../../../../../components/organisms/ProductionPlans/Gantt/hooks';
import {
  AVAILABLE_QUERY_PARAMS,
  useProductionPlanQueryParams,
  useProductionPlanSearchStore,
} from '../../../../../../../../components/organisms/ProductionPlans';
import { TaskNodeBase, TaskStatus } from '@nexelem/graphne-api';
import { useTaskAvailability } from '../../../../../hooks';
import { formatUnit } from '../../../../../../../../components/organisms/ProductionPlans/Kanban/components/KanbanTask';

type ProductionLotItemProps = {
  previousTasks?: (TaskNodeBase & { nodeType: 'TaskNode' })[];
  nextTasks?: (TaskNodeBase & { nodeType: 'TaskNode' })[];
  isGraphDrawer: boolean;
  isKanban: boolean;
};

export const ProductionLotItem = ({
  previousTasks = [],
  nextTasks = [],
  isGraphDrawer,
  isKanban,
}: ProductionLotItemProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const dateFormatter = useDateFormatter({ displayWithHours: true });
  const locateTask = useProductionTaskLocate();
  const { setQueryParams } = useProductionPlanQueryParams();
  const setSearchResults = useProductionPlanSearchStore((state) => state.setSearchResults);
  const { getTaskAvailabilityInfo } = useTaskAvailability(isKanban);

  const allTasks = [...previousTasks, ...nextTasks];

  if (allTasks.length === 0) {
    return null;
  }

  const renderTask = (productionLot: TaskNodeBase & { nodeType: 'TaskNode' }, isPrevious: boolean) => {
    const availabilityInfo = getTaskAvailabilityInfo(productionLot);
    const isTaskAvailable = availabilityInfo.isAvailable;

    const handleOpenDrawer = () => {
      // Update query params first to highlight the new task
      if (productionLot.queue?.id) {
        setQueryParams([
          {
            key: AVAILABLE_QUERY_PARAMS.queueId,
            value: productionLot.queue.id,
          },
          {
            key: AVAILABLE_QUERY_PARAMS.taskId,
            value: productionLot.id,
          },
        ]);

        locateTask({
          queueId: productionLot.queue.id,
          taskId: productionLot.id,
          taskStartDate: productionLot.start ? new Date(productionLot.start) : undefined,
        });
      }

      openTaskDetailsDrawer({
        taskId: productionLot.id.toString(),
        isGraphDrawer: isGraphDrawer,
      });
    };

    const handleNavigateToTask = () => {
      if (productionLot.queue?.id) {
        setSearchResults([productionLot.id]);

        locateTask({
          queueId: productionLot.queue.id,
          taskId: productionLot.id,
        });
      }
    };

    const currentAmount = productionLot.progress?.current;
    const totalAmount = productionLot.progress?.totalAmount?.amount;
    const unit = productionLot.progress?.totalAmount?.unit?.name;
    const formattedAmount =
      currentAmount !== undefined && totalAmount !== undefined && unit
        ? `${formatUnit(currentAmount)}/${formatUnit(totalAmount)} ${unit}`
        : undefined;
    const stationName = productionLot.queue
      ? `${productionLot.queue.area?.name || '-'} ${productionLot.queue.shortName}`
      : tWrapper('station');
    const isNotPlanned = productionLot.status === TaskStatus.NotPlanned;

    const hasQueue = !!productionLot.queue?.id;
    const hasEndDate = !!productionLot.end;
    const shouldHideButtons = !isTaskAvailable;
    const shouldShowHiddenBadge = !hasQueue || (!isTaskAvailable && availabilityInfo.reason === 'differentQueue');
    const shouldShowOutOfTimeBadge = !hasEndDate || (!isTaskAvailable && availabilityInfo.reason === 'timeRange');

    const statusColor = GanttUtils.getTaskStatusColor(productionLot.status, productionLot.errors);
    const colorMap: Record<string, string> = {
      green: 'text-border-status-green-darker',
      yellow: 'text-border-status-yellow-darker',
      blue: 'text-border-status-blue-darker',
      red: 'text-border-status-red-darker',
      grey: 'text-border-status-grey-darker',
    };
    const amountColorClass = colorMap[statusColor] || 'text-text-secondary';

    return (
      <div
        className="flex items-center gap-3 border-b border-border-container py-3 pr-2 pl-4"
        key={productionLot.nodeId}
      >
        <NodeIcon
          statusColor={GanttUtils.getTaskStatusColor(productionLot.status, productionLot.errors)}
          taskType={productionLot.taskType}
        />
        <div className="flex grow flex-col gap-1">
          <span className="flex items-center gap-2 text-s16-sb text-text-primary">{productionLot.name}</span>
          <span className="text-s11-sb text-text-primary uppercase">{productionLot.productDetails?.name || '-'}</span>
          {formattedAmount && <span className={`text-s11-sb uppercase ${amountColorClass}`}>{formattedAmount}</span>}
          <span className="text-s11-sb text-text-secondary uppercase">
            {tWrapper(isPrevious ? 'productionLotPrevious' : 'productionLotNext')}
          </span>
          {isNotPlanned ? (
            <Badge className="self-start">{tWrapper('notPlanned')}</Badge>
          ) : (
            <>
              <span className="inline-flex items-center gap-2 text-s11-sb text-text-secondary uppercase">
                {tWrapper('productionEnd')} {dateFormatter(productionLot.end)}
                {shouldShowOutOfTimeBadge && (
                  <Badge
                    className="whitespace-nowrap"
                    StartIcon={<FontAwesomeIcon className="text-icon-primary" icon={faEye} />}
                  >
                    {tWrapper('outOfTimeSet')}
                  </Badge>
                )}
              </span>
              <span className="inline-flex items-center gap-2 text-s11-sb text-text-secondary uppercase">
                {stationName}
                {shouldShowHiddenBadge && (
                  <Badge
                    className="whitespace-nowrap"
                    StartIcon={<FontAwesomeIcon className="text-icon-primary" icon={faEye} />}
                  >
                    {tWrapper('hidden')}
                  </Badge>
                )}
              </span>
            </>
          )}
        </div>
        {!shouldHideButtons && (
          <div className="flex gap-2 self-start">
            <IconButton Icon={() => <FontAwesomeIcon icon={faInfoCircle} />} size="small" onClick={handleOpenDrawer} />
            {!isGraphDrawer && (
              <IconButton
                Icon={() => <FontAwesomeIcon icon={faCircleLocationArrow} />}
                size="small"
                onClick={handleNavigateToTask}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {previousTasks.map((task) => renderTask(task, true))}
      {nextTasks.map((task) => renderTask(task, false))}
    </>
  );
};
